import type { DerivedSurface, LookScheme } from "@/types/look-system";
import { surfaceOf } from "./color-system";
import { paletteSchemes } from "./colour-versions";
import { isCssColour } from "./css-colour";
import { isLookSystem } from "./look-gate";

export type LivingEffect =
  | "nordlys"
  | "glod"
  | "prikker"
  | "farveflow"
  | "silke"
  | "bolger";
export type LivingPhones = "live" | "still";
export type LivingQuality = "spar" | "auto" | "hoej";
/** `still` is the effect's first frame, standing; `shader` draws it with WebGL, `css` with living.css. */
export type LivingMode = "still" | "css" | "shader";

export interface LivingBackground {
  effect: LivingEffect;
  colors: [string, string, string];
  /** Seconds per cycle. */
  speed: number;
  strength: number;
  size: number;
  direction: number;
  softness: number;
  followPointer: boolean;
  phones: LivingPhones;
  quality: LivingQuality;
  pause: boolean;
  color?: string;
}

export interface BackgroundDrift {
  speed: number;
  phones: LivingPhones;
  pause: boolean;
}

/** The scrim's opacity in % per scheme; 0 where the worst frame is readable. */
export interface LivingScrim {
  light: number;
  dark: number;
}

export interface LivingEnvironment {
  reducedMotion: boolean;
  saveData: boolean;
  webgl: boolean;
  phone: boolean;
  lowPower: boolean;
  finePointer: boolean;
  /** No other living background on the page holds the one shader. */
  shaderFree: boolean;
}

export const LIVING_EFFECTS: readonly LivingEffect[] = [
  "nordlys",
  "glod",
  "prikker",
  "farveflow",
  "silke",
  "bolger",
];
export const SHADER_EFFECTS: readonly LivingEffect[] = ["silke", "bolger"];
/** The derived link, eyebrow and heading colours, so a background without its own colours follows the palette. */
export const LIVING_THEME_COLOURS: [string, string, string] = [
  "var(--re-link)",
  "var(--re-eyebrow)",
  "var(--re-heading)",
];
/** The cards' seconds per cycle; exact values go from 10 s, never faster on a full-width background (spec §9.5). */
export const LIVING_SPEEDS = { slow: 40, normal: 20, fast: 10 } as const;
const SPEED_RANGE: [number, number] = [10, 120];
export const NO_SCRIM: LivingScrim = { light: 0, dark: 0 };

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

function clamp(value: unknown, [min, max]: [number, number], fallback: number) {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback;
}

function colour(value: unknown): string | undefined {
  const text = typeof value === "string" ? value.trim() : undefined;
  return isCssColour(text) ? text : undefined;
}

const phonesOf = (value: unknown): LivingPhones =>
  value === "still" ? "still" : "live";

const speedOf = (value: unknown) =>
  clamp(value, SPEED_RANGE, LIVING_SPEEDS.normal);

/** Never throws; null for anything that is not `{ kind: "living" }`. */
export function resolveLivingBackground(
  input: unknown,
): LivingBackground | null {
  const value = record(input);
  if (value.kind !== "living") return null;
  const living = record(value.living);
  const effect =
    LIVING_EFFECTS.find((name) => name === living.effect) ?? "nordlys";
  const own = Array.isArray(living.colors) ? living.colors : [];
  const base = colour(value.color);
  return {
    effect,
    colors: LIVING_THEME_COLOURS.map(
      (fallback, index) => colour(own[index]) ?? fallback,
    ) as [string, string, string],
    speed: speedOf(living.speed),
    strength: clamp(living.strength, [10, 100], 55),
    size: clamp(living.size, [50, 200], 100),
    direction: clamp(living.direction, [0, 359], 120),
    softness: clamp(living.softness, [0, 120], 40),
    followPointer: effect === "glod" && living.followPointer === true,
    phones: phonesOf(living.phones),
    quality:
      living.quality === "spar" || living.quality === "hoej"
        ? living.quality
        : "auto",
    pause: living.pause !== false,
    ...(base ? { color: base } : {}),
  };
}

/** A page background's `drift`; undefined without a speed, so today's effects stand still as before. */
export function resolveDrift(input: unknown): BackgroundDrift | undefined {
  const drift = record(input);
  if (typeof drift.speed !== "number" || !Number.isFinite(drift.speed))
    return undefined;
  return {
    speed: speedOf(drift.speed),
    phones: phonesOf(drift.phones),
    pause: drift.pause !== false,
  };
}

export function livingStyle(living: LivingBackground): Record<string, string> {
  return {
    ...(living.color ? { "--re-living-base": living.color } : {}),
    "--re-living-1": living.colors[0],
    "--re-living-2": living.colors[1],
    "--re-living-3": living.colors[2],
    "--re-living-duration": `${living.speed}s`,
    "--re-living-strength": `${living.strength}%`,
    "--re-living-size": `${living.size / 100}`,
    "--re-living-direction": `${living.direction}deg`,
    "--re-living-softness": `${living.softness}px`,
  };
}

export function scrimStyle(scrim: LivingScrim | undefined) {
  return scrim && (scrim.light > 0 || scrim.dark > 0)
    ? {
        "--re-living-scrim": `${scrim.light}%`,
        "--re-living-scrim-dark": `${scrim.dark}%`,
      }
    : undefined;
}

export function livingMode(
  living: Pick<LivingBackground, "effect" | "phones" | "quality">,
  env: LivingEnvironment,
): LivingMode {
  if (env.reducedMotion || env.saveData) return "still";
  if (env.phone && living.phones === "still") return "still";
  if (!SHADER_EFFECTS.includes(living.effect)) return "css";
  if (!env.webgl) return "still";
  const light =
    living.quality === "spar" ||
    (living.quality === "auto" && (env.phone || env.lowPower));
  return light || !env.shaderFree ? "css" : "shader";
}

const webglSupport = new WeakMap<object, boolean>();

function hasWebgl(win: Window) {
  const known = webglSupport.get(win);
  if (known !== undefined) return known;
  let found = false;
  try {
    found = Boolean(win.document.createElement("canvas").getContext("webgl"));
  } catch {
    found = false;
  }
  webglSupport.set(win, found);
  return found;
}

/** Everything livingMode needs from the browser but the shader slot. */
export function livingEnvironment(
  win: Window,
): Omit<LivingEnvironment, "shaderFree"> {
  const query = (media: string) => win.matchMedia?.(media).matches === true;
  const nav = win.navigator as Navigator & {
    connection?: { saveData?: boolean };
    deviceMemory?: number;
  };
  return {
    reducedMotion: query("(prefers-reduced-motion: reduce)"),
    saveData: nav?.connection?.saveData === true,
    webgl: hasWebgl(win),
    phone: query("(max-width: 767px)"),
    lowPower:
      (nav?.hardwareConcurrency ?? 8) < 4 || (nav?.deviceMemory ?? 8) < 4,
    finePointer: query("(pointer: fine)"),
  };
}

let shaderOwner: object | undefined;

/** One WebGL background per page: the first to claim the shader keeps it until it lets go. */
export function claimShader(owner: object): boolean {
  if (shaderOwner && shaderOwner !== owner) return false;
  shaderOwner = owner;
  return true;
}

export function releaseShader(owner: object) {
  if (shaderOwner === owner) shaderOwner = undefined;
}

/** Marks `element` with data-re-living-idle while it is off-screen or the tab is hidden; returns the cleanup. */
export function watchLivingIdle(element: Element, win: Window): () => void {
  let visible = true;
  const update = () =>
    element.toggleAttribute(
      "data-re-living-idle",
      !visible || win.document.hidden,
    );
  const observer = win.IntersectionObserver
    ? new win.IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        visible = entries[entries.length - 1]?.isIntersecting ?? visible;
        update();
      })
    : undefined;
  observer?.observe(element);
  win.document.addEventListener("visibilitychange", update);
  return () => {
    observer?.disconnect();
    win.document.removeEventListener("visibilitychange", update);
  };
}

interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const OPACITY_MIX =
  /^color-mix\(in srgb,\s*(.+?)\s+(\d{1,3}(?:\.\d+)?)%,\s*transparent\)$/i;
const PALETTE_REF = /^var\(--re-p-([a-z0-9-]{1,40})\)$/;
const ROLE_REF =
  /^var\(--re-(text|heading|muted|card|border|control|link|eyebrow|focus)\)$/;

function rgba(value: unknown): Rgba | undefined {
  const digits = typeof value === "string" ? HEX.exec(value)?.[1] : undefined;
  if (!digits) return undefined;
  const pairs =
    digits.length <= 4 ? [...digits].map((d) => d + d) : digits.match(/../g)!;
  const [r, g, b, a = 255] = pairs.map((pair) => parseInt(pair, 16));
  return { r, g, b, a: a / 255 };
}

function paletteHex(
  colorSystem: Record<string, unknown>,
  key: string,
  scheme: LookScheme,
) {
  const entries = Array.isArray(colorSystem.palette) ? colorSystem.palette : [];
  const redirects = record(colorSystem.redirects);
  const seen = new Set<string>();
  for (
    let current: unknown = key;
    typeof current === "string" && !seen.has(current);
    current = redirects[current]
  ) {
    const entry = entries.map(record).find((item) => item.key === current);
    if (entry)
      return paletteSchemes(colorSystem, entry)?.[scheme].colour ?? entry.hex;
    seen.add(current);
  }
  return undefined;
}

/** What the colour shows in `scheme` on `surface`, or undefined where only the browser could know. */
function shown(
  value: string,
  scheme: LookScheme,
  surface: DerivedSurface,
  colorSystem: Record<string, unknown>,
): Rgba | undefined {
  const mix = OPACITY_MIX.exec(value);
  if (mix) {
    const inner = shown(mix[1], scheme, surface, colorSystem);
    return (
      inner && { ...inner, a: (inner.a * Math.min(100, Number(mix[2]))) / 100 }
    );
  }
  const key = PALETTE_REF.exec(value)?.[1];
  if (key) return rgba(paletteHex(colorSystem, key, scheme));
  const role = ROLE_REF.exec(value)?.[1] as keyof DerivedSurface | undefined;
  return rgba(role ? surface[role] : value);
}

const over = (top: Rgba, below: Rgba): Rgba => ({
  r: top.r * top.a + below.r * (1 - top.a),
  g: top.g * top.a + below.g * (1 - top.a),
  b: top.b * top.a + below.b * (1 - top.a),
  a: 1,
});

function luminance({ r, g, b }: Rgba) {
  const [lr, lg, lb] = [r, g, b].map((value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

function contrast(a: Rgba, b: Rgba) {
  const [high, low] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (high + 0.05) / (low + 0.05);
}

/** Each field at its strongest, alone and over another one, as the fields cross while they move. */
function frames(colours: Rgba[], base: Rgba, strength: number): Rgba[] {
  const fields = colours.map((field) => ({ ...field, a: field.a * strength }));
  return fields.flatMap((field, index) => [
    over(field, base),
    ...fields
      .filter((_, other) => other !== index)
      .map((other) => over(other, over(field, base))),
  ]);
}

function schemeScrim(
  living: LivingBackground,
  scheme: LookScheme,
  surface: DerivedSurface,
  colorSystem: Record<string, unknown>,
  needed: number,
): number {
  const known = (value: string) => shown(value, scheme, surface, colorSystem);
  const base = (living.color && known(living.color)) || rgba(surface.bg);
  const inks = [surface.text, surface.heading].map(rgba);
  if (!base || inks.some((ink) => !ink)) return 0;
  const readable = (frame: Rgba) =>
    inks.every((ink) => contrast(ink!, frame) >= needed);
  if (!readable({ ...base, a: 1 })) return 0;
  const worst = frames(
    living.colors.map(known).filter((value): value is Rgba => !!value),
    { ...base, a: 1 },
    living.strength / 100,
  );
  for (let percent = 0; percent < 100; percent += 5)
    if (
      worst.every((frame) =>
        readable(over({ ...base, a: percent / 100 }, frame)),
      )
    )
      return percent;
  return 95;
}

/**
 * The scrim of the base colour a living background needs so its text stays readable in its worst frame, against the
 * derived text and heading colours the CMS's engine stored for the background (`key`, the page's when absent, or a
 * section's own colour's `own` = `ui.lookDerived`). Colours only the browser could resolve are left out.
 */
export function livingScrim(
  theme: unknown,
  living: LivingBackground,
  key = "page",
  own?: unknown,
): LivingScrim {
  if (!isLookSystem(theme)) return NO_SCRIM;
  const colorSystem = record(theme.colorSystem);
  const derived = record(colorSystem.derived);
  const needed = record(colorSystem.options).contrast === "high" ? 7 : 4.5;
  const scrim = (scheme: LookScheme) => {
    const surfaces = record(derived[scheme]);
    const surface =
      surfaceOf(record(own)[scheme]) ??
      surfaceOf(surfaces[key]) ??
      surfaceOf(surfaces.page);
    return surface
      ? schemeScrim(living, scheme, surface, colorSystem, needed)
      : 0;
  };
  return { light: scrim("light"), dark: scrim("dark") };
}
